import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import HBInput from '@honeybook/hbui/dist/components/HBInput/HBInput';
import { observer } from 'mobx-react';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import { LabelSpan } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.styles';
import { TSize } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.types';
import { TTextFieldSize } from '@honeybook/hbui/dist/components/HBTextField/variables';
import colors from '@honeybook/hbui/dist/styles/colors';
import {
    AUTOMATION_TRIGGER_DELAY_PREPOSITION,
    AUTOMATION_TRIGGER_DELAY_UNITS,
    getAutomationTriggerEvents,
    groupedDropdownCustomStyles,
    IAutomationTriggerEvent
} from '../../../../../utils/automation.utils';

import { AnalyticsEvents } from '../../../../../services/analytics/analytics-events';
import useAnalytics from '../../../../../common/useAnalytics';
import { EDIT_AUTOMATION_TOOLTIP } from '../StepSideBar/StepSideBar.styles';
import {
    TAutomationTriggerEventGroup,
    TAutomationTriggerPreposition,
    TAutomationTriggerType,
    TAutomationTriggerUnit
} from '../../../../../types/automation.types';
import HBDropdown from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown';
import { useRootStore } from '~/stores/useRootStore';
import { groupBy, map } from 'lodash';
import { TFunction, useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import HBPill from '@honeybook/hbui/dist/components/HBPill/HBPill';
import { PillType } from '@honeybook/hbui/dist/components/HBPill/HBPill.types';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { IFlowsBetaUserType } from '~/models/user.model';

// Note:
// The UI splits the preposition ('after' or 'before') from the
// full trigger name even though it is the same data location.
// E.g. User picks 'after' and 'project_end_date' instead of 'after_project_end_date'
// This requires some peculiar parsing on the preposition and the trigger_type

const _formatPhraseOptions = (
    afterOrBefore: TAutomationTriggerPreposition,
    stepOrder: number,
    userType: IFlowsBetaUserType,
    t: TFunction<[typeof NAMESPACES.AUTOMATIONS, typeof NAMESPACES.GLOBALS]>
) => {
    const isMatchingPreposition = (trigger: IAutomationTriggerEvent) =>
        trigger.preposition === afterOrBefore;
    const removePrepositionFromLabel = (trigger: IAutomationTriggerEvent) => {
        let triggerLabel = t(`automations:trigger_types.${trigger.value}`);
        triggerLabel = triggerLabel
            .split(' ')
            .slice(1, triggerLabel.length - 1)
            .join(' ');

        return {
            ...trigger,
            label: (
                <>
                    {triggerLabel}
                    {trigger.isDisabled && (
                        <HBPill
                            theme={PillType.Sent}
                            label={t('globals:common.coming_up')}
                            testIdName="coming-up"
                        />
                    )}
                </>
            )
        };
    };

    return getAutomationTriggerEvents(userType, stepOrder)
        .filter(isMatchingPreposition)
        .map(removePrepositionFromLabel);
};

const TriggerDatePicker = () => {
    const {
        automationUiStore: {
            tooltipCommonProps,
            isSelectedStepEditingDisabled,
            selectedStep: {
                id: stepId,
                order,
                combinedType,
                updateStepTrigger,
                workflow_step_trigger
            }
        }
    } = useRootStore();
    const {
        userStore: { currUser }
    } = useGlobalMX();

    const userType = currUser.flowsBetaUserType;

    const { track } = useAnalytics();

    const { t } = useTranslation([NAMESPACES.AUTOMATIONS, NAMESPACES.GLOBALS]);

    const { delay_temporal_amount, delay_temporal_unit, trigger_type } =
        workflow_step_trigger || {};

    const isSingular = delay_temporal_amount === 1;

    const automationTriggerDelayUnits = AUTOMATION_TRIGGER_DELAY_UNITS.map(
        unit => ({
            ...unit,
            label: isSingular ? unit.labelSingular : unit.labelPlural
        })
    );

    const selectedPreposition = trigger_type?.split(
        '_'
    )[0] as TAutomationTriggerPreposition;

    let delayPhraseOptions = _formatPhraseOptions(
        selectedPreposition,
        order,
        userType,
        t
    );

    const triggerAnalytics = (source, value) => {
        track(AnalyticsEvents.AutomationsSideBarChangeTriggerTime, {
            action: 'click',
            type: combinedType,
            source,
            value
        });
    };

    const [delayValue, setDelayValue] = useState('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setDelayValue(delay_temporal_amount.toString()), [stepId]);

    const handleChangeDelayNumber = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const valueAsNumber = Number(value);
        if (value.includes('.') || !Number.isInteger(valueAsNumber)) {
            return;
        }
        setDelayValue(value);
        triggerAnalytics('amount', valueAsNumber);
        updateStepTrigger({ delay_temporal_amount: valueAsNumber });
    };

    const handleChangeDelayUnit = (value: TAutomationTriggerUnit) => {
        triggerAnalytics('temporal_unit', value);
        updateStepTrigger({ delay_temporal_unit: value });
    };

    const handleChangeDelayPreposition = (
        value: TAutomationTriggerPreposition
    ) => {
        triggerAnalytics('before/after', value);
        delayPhraseOptions = _formatPhraseOptions(value, order, userType, t);
        updateStepTrigger({ trigger_type: delayPhraseOptions[0]?.value });
    };

    const handleChangeTrigger = (value: TAutomationTriggerType) => {
        triggerAnalytics('stage', value);
        updateStepTrigger({ trigger_type: value });
    };

    const commonProps: { size: TSize & TTextFieldSize; disabled: boolean } = {
        size: 'sm',
        disabled: isSelectedStepEditingDisabled
    };

    const groupedDelayPhraseOptions = map(
        groupBy(delayPhraseOptions, 'group'),
        (value, key) => ({
            label: t(
                `automations:trigger_group_type.${
                    key as TAutomationTriggerEventGroup
                }`
            ),
            options: value
        })
    );

    if (combinedType === 'pipeline') {
        delete groupedDelayPhraseOptions[0].options[3];
    }

    return (
        <HBTooltip
            {...tooltipCommonProps}
            target={
                <DropdownSection>
                    <DropdownRow>
                        <DelayNumberInput
                            testIdName="trigger-delay-value"
                            type="text"
                            value={delayValue}
                            onChange={handleChangeDelayNumber}
                            {...commonProps}
                        />
                        <DelayDropdown
                            testIdName="trigger-delay-unit"
                            options={automationTriggerDelayUnits}
                            value={delay_temporal_unit}
                            onChange={handleChangeDelayUnit}
                            {...commonProps}
                        />
                        <DelayDropdown
                            testIdName="trigger-delay-preposition"
                            options={AUTOMATION_TRIGGER_DELAY_PREPOSITION}
                            value={selectedPreposition}
                            onChange={handleChangeDelayPreposition}
                            {...commonProps}
                        />
                    </DropdownRow>
                    <DropdownRow>
                        <HBDropdown
                            testIdName="trigger-type"
                            options={groupedDelayPhraseOptions}
                            minMenuHeight={230}
                            maxMenuHeight={230}
                            value={trigger_type}
                            onChange={handleChangeTrigger}
                            dropdownIndicatorColor={colors.neutral900}
                            customStyles={groupedDropdownCustomStyles}
                            menuPlacement="auto"
                            menuShouldScrollIntoView={false}
                            {...commonProps}
                        />
                    </DropdownRow>
                </DropdownSection>
            }
        >
            {EDIT_AUTOMATION_TOOLTIP}
        </HBTooltip>
    );
};

// -------------------------------- Styles -------------------------------- //

const DropdownSection = styled.section`
    margin-bottom: 15px;
`;

const DropdownRow = styled.div`
    display: flex;
    padding-bottom: 5px;
    gap: 5px;
`;

const DelayNumberInput = styled(HBInput)`
    &&&& {
        border-width: 0;
        &:hover,
        &:focus {
            background-color: ${colors.neutral200};
        }
        &:not(:disabled) {
            color: ${colors.neutral900};
        }
        &:disabled {
            pointer-events: none;
            background-color: ${colors.neutral100};
        }
    }
`;

const DelayDropdown = styled(HBDropdown)`
    && {
        min-width: 90px;
    }
`;

export default observer(TriggerDatePicker);
